import React from 'react'
import styles from './TRouteSidePanel.scss'
import { TRouteSidePanelActions } from './TRouteSidePanelActions'
import { TRouteAtAGlance } from './TRouteAtAGlance'
import { TRouteMeta } from './TRouteMeta'
import { TRoutePhotos } from './TRoutePhotos'
import { TRouteCues } from './TRouteCues'
import { TRouteHills } from './TRouteHills'
import { TRouteComments } from './TRouteComments'
import { TRouteSegments } from './TRouteSegments'
import { SignupPrompt } from './SignupPrompt'
import { NavigationInfo } from './NavigationInfo'
import { TripMetrics } from './TripMetrics'
import { TripLaps } from './TripLaps'
import { CollapseMap } from './CollapseMap'
import { SmartExport } from 'common/SmartExport'
import { EDIT_TROUTE_MODAL } from 'common/modals/EditTRouteModal'
import { ROLE_SIDE_PANEL } from 'common/layouts/ThreePanelLayout'
import { StickyHeaderSidePanel } from 'common/layouts/ThreePanelLayout/StickyHeaderSidePanel'
import { OpenInAppButton } from 'common/OpenInAppButton'
import { Icon } from 'common/Icon'
import { HeartButton } from 'common/HeartButton'
import { formatText } from 'lib/textUtils'
import { showModal } from 'lib/modalUtils'
import { Msg, useFormatMessage, commonVerbs, commonMessages } from 'lib/localization'
import { userAgentIsMobile } from 'lib/mobileDeviceChecks'
import { orgVisibility, userVisibility } from 'lib/visibility'
import queryString from 'query-string'
import moment from 'moment'
import { injectIntl, defineMessages } from 'react-intl'
import cx from 'classnames'
import { withRouter } from 'react-router-dom'

const headerMessage = defineMessages({
  locked: {
    id: 'headerMessage.locked',
    defaultMessage: 'This route has been locked by an administrator and cannot be modified'
  }
})

const ScrollingHeader = ({ troute, objectType, mapRef }) => {
  const allowInlineEdits = troute.permissions && troute.permissions.canBeModified && !troute.locked
  const showEditModal = () => showModal(EDIT_TROUTE_MODAL, { troute, objectType })
  const visibility = troute.visibility > 0 ? (troute.club ? orgVisibility : userVisibility)[troute.visibility] : ''
  const fm = useFormatMessage()

  return (
    <div className={styles.ScrollingHeader}>
      <TRouteSidePanelActions mapRef={mapRef} troute={troute} objectType={objectType} />
      <div className={styles.titleAuthor}>
        <h1 onClick={allowInlineEdits && showEditModal} className={styles.editTitle}>
          {troute.name} {allowInlineEdits
            ? <Icon className={styles.icon} i='md-create' size={18} />
            : (troute.locked && <Icon classname={styles.icon} i='md-lock' size={20} data-tip title={fm(headerMessage.locked)} />)}
        </h1>
        <div className={styles.byline}>
          <Msg m={commonMessages.byline} values={{ author: <a href={troute.bylinePath}>{troute.bylineName}</a> }} />
          {visibility && <span
            className={cx(styles.privacyBadge, allowInlineEdits && styles.pointer)}
            onClick={() => allowInlineEdits && showModal(EDIT_TROUTE_MODAL, { troute, objectType })}>
            {visibility}
          </span>}
        </div>
        {troute.departedAt && <div className={styles.departedAt}>{moment.parseZone(troute.departedAt).format('MMMM D, YYYY, h:mm a')}</div>}
      </div>
    </div>
  )
}

const StickyHeader = ({ troute, objectType, mapRef }) => (
  <div className={styles.StickyHeader}>
    <h1>{troute.name}</h1>
    <TRouteSidePanelActions mini troute={troute} objectType={objectType} mapRef={mapRef} />
  </div>
)

const OpenRouteInAppButton = ({ route, ...props }) => {
  const query = queryString.stringify({
    privacy_code: route.privacyCode ? route.privacyCode : undefined
  })
  const appLink = query
    ? `rwgps://app/routes/${route.id}?${query}`
    : `rwgps://app/routes/${route.id}`

  return <OpenInAppButton appLink={appLink} {...props} />
}

class _TRouteSidePanel extends React.PureComponent {
  static role = ROLE_SIDE_PANEL

  render () {
    const {
      objectType,
      onTriggerFileBrowser,
      pendingPhotoUploads,
      troute,
      troutePhotos,
      troutePOIs,
      mapRef,
      onPhotoDeleted,
      onFeatureHovered,
      onFeatureSelected,
      currentUser,
      selectedFeature,
      history,
      intl
    } = this.props

    const reallyCanEdit = troute.permissions && troute.permissions.canBeModified && !troute.locked

    return (
      <StickyHeaderSidePanel
        stickyHeader={<StickyHeader troute={troute} objectType={objectType} mapRef={mapRef} />}
        scrollingHeader={<ScrollingHeader troute={troute} objectType={objectType} mapRef={mapRef} />}>
        <div className={styles.TRouteSidePanel}>
          <TRouteAtAGlance troute={troute} objectType={objectType} onFeatureHovered={onFeatureHovered} />
          <CollapseMap troute={troute} troutePhotos={troutePhotos} troutePOIs={troutePOIs} />
          <div className={styles.padWrap}>
            {objectType === 'route' && <>
              <div className={styles.export}>
                {(userAgentIsMobile()
                  ? <OpenRouteInAppButton margins={false} route={troute} />
                  : <SmartExport
                    route={troute}
                    sendToDevice={() => history.push(`/routes/${troute.id}/send_to_device`)} />)}
              </div>
              <hr />
            </>}
            {troute.description && troute.description.trim() !== '' && <>
              <div className={styles.description}>
                {formatText(troute.description)}
                {reallyCanEdit && <p>
                  <a className={styles.editLink} onClick={() => showModal(EDIT_TROUTE_MODAL, { troute, objectType })}>
                    <Msg m={commonVerbs.edit} />
                  </a>
                </p>}
              </div>
              <hr />
            </>}
            <TRouteMeta troute={troute} objectType={objectType} editable={reallyCanEdit} intl={intl} />
            <TRoutePhotos
              editable={reallyCanEdit}
              objectType={objectType}
              troute={troute}
              onTriggerFileBrowser={onTriggerFileBrowser}
              pendingPhotoUploads={pendingPhotoUploads}
              photos={troutePhotos}
              onPhotoDeleted={onPhotoDeleted} />
            {objectType === 'trip' && troute.navId && <NavigationInfo troute={troute} objectType={objectType} />}
            {!currentUser.id && <SignupPrompt />}
            {objectType === 'trip' && <TripMetrics
              trip={troute}
              onFeatureHovered={onFeatureHovered}
              selectedFeature={selectedFeature} />}
            {objectType === 'route' && <TRouteCues
              troute={troute}
              onFeatureHovered={onFeatureHovered}
              onFeatureSelected={onFeatureSelected} />}
            <TRouteSegments
              objectType={objectType}
              troute={troute}
              onFeatureHovered={onFeatureHovered}
              onFeatureSelected={onFeatureSelected} />
            {objectType === 'trip' && <TripLaps
              trip={troute}
              onFeatureHovered={onFeatureHovered}
              onFeatureSelected={onFeatureSelected} />}
            <TRouteHills
              troute={troute}
              onFeatureHovered={onFeatureHovered}
              onFeatureSelected={onFeatureSelected} />
            <HeartButton
              objectType={objectType}
              objectId={troute.id}
              liked={troute.likedByCurrentUser}
              count={troute.likesCount} />
            <TRouteComments troute={troute} objectType={objectType} />
          </div>
        </div>
      </StickyHeaderSidePanel>
    )
  }
}

export const TRouteSidePanel = withRouter(injectIntl(_TRouteSidePanel))
